import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'sonner';
import { Loader2, Mail, MessageSquare, Smartphone } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { signInWithGoogle, setOnNameInputComplete } from '@/lib/firebase/auth/google';
import { requestPasswordReset } from '@/lib/firebase/auth/passwordReset';
import { NameInputDialogWithOTP } from './NameInputDialogWithOTP';
import { sendWhatsAppOTP, verifyWhatsAppOTP } from '@/lib/firebase/auth/whatsapp-phone-auth';
import { OTPInput } from '@/components/ui/otp-input';
import { useLoginMethods } from '@/hooks/use-login-methods';
import { useOTPMethod } from '@/hooks/use-otp-method';
import type { UserData } from '@/lib/firebase/auth/types';
import type { OTPMethod } from '@/lib/services/authentica';
import { resolveOtpChannel } from '@/lib/auth/otpChannel';

type LoginMethod = 'password' | 'whatsapp';

export function LoginForm() {
  // All useState hooks first (fixed order)
  const [loading, setLoading] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [newUserData, setNewUserData] = useState<UserData | null>(null);
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('whatsapp');
  const [otpSent, setOtpSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [otpFailed, setOtpFailed] = useState(false);
  const [sentVia, setSentVia] = useState<OTPMethod | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotInput, setForgotInput] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // All context hooks (fixed order)
  const { login, loginWithWhatsAppOTP, setUserData } = useAuth();
  const { t, dir, language } = useLanguage();
  const { settings: loginMethodsSettings, loading: loginMethodsLoading } = useLoginMethods();
  const { otpMethod: otpSetting, loading: otpSettingLoading } = useOTPMethod();
  // #22: name the channel the admin chose / the server used; neutral while unknown.
  const otpMethod = resolveOtpChannel(otpSetting, otpSettingLoading, sentVia);
  
  // Check if coming from volunteer registration route
  const isFromVolunteerRoute = sessionStorage.getItem('redirectAfterLogin') === '/vol';
  
  // Determine available methods and set default
  const availableMethods = {
    whatsapp: loginMethodsSettings.phoneOtp,
    password: loginMethodsSettings.emailPassword
  };
  
  // Get default method (prefer whatsapp if available, otherwise password)
  const getDefaultMethod = (): LoginMethod => {
    if (availableMethods.whatsapp) return 'whatsapp';
    if (availableMethods.password) return 'password';
    return 'whatsapp'; // fallback
  };
  
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const verifyingRef = useRef(false);

  // All useEffect hooks (fixed order)
  useEffect(() => {
    setOnNameInputComplete((userData) => {
      setNewUserData(userData);
      setShowNameDialog(true);
    });
  }, []);

  // Set default login method when settings are loaded
  useEffect(() => {
    if (!loginMethodsLoading) {
      const defaultMethod = getDefaultMethod();
      setLoginMethod(defaultMethod);
    }
  }, [loginMethodsLoading, loginMethodsSettings]);

  // Static form schemas (moved after hooks to avoid recreation issues)
  const passwordFormSchema = z.object({
    email: z
      .string()
      .min(1, 'Email or phone number is required')
      .refine((value) => {
        // Check if it's a valid email
        const emailResult = z.string().email().safeParse(value);
        if (emailResult.success) return true;
        
        // Check if it's a valid phone number (05XXXXXXXX)
        const phoneResult = z.string().regex(/^05\d{8}$/).safeParse(value);
        return phoneResult.success;
      }, 'Please enter a valid email address or phone number (05XXXXXXXX)'),
    password: z.string().min(6, t('auth.passwordMinLength')),
  });

  const whatsappFormSchema = z.object({
    phone: z.string()
      .min(9, t('auth.phoneMinLength'))
      .max(10, t('auth.phoneMaxLength'))
      .regex(/^05\d{8}$/, t('auth.phoneMustBeNumbers')),
    otp: z.string().optional()
  }).refine((data) => {
    // Only validate OTP if it's provided (not empty)
    if (data.otp && data.otp.length > 0) {
      return data.otp.length === 4 && /^\d{4}$/.test(data.otp);
    }
    return true;
  }, {
    message: t('auth.codeMustBe4Digits'),
    path: ['otp']
  });

  // Form hooks (must be after schemas)
  const passwordForm = useForm<z.infer<typeof passwordFormSchema>>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const whatsappForm = useForm<z.infer<typeof whatsappFormSchema>>({
    resolver: zodResolver(whatsappFormSchema),
    defaultValues: {
      phone: '',
      otp: ''
    }
  });

  async function onSubmitPassword(values: z.infer<typeof passwordFormSchema>) {
    try {
      setLoading(true);
      await login(values.email, values.password);
      // The redirect will be handled by the auth state change in AuthContext
    } catch (error) {
      toast.error((error as Error)?.message || t('auth.signInError'));
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    setForgotLoading(true);
    try {
      const r = await requestPasswordReset(forgotInput);
      if (r.ok) {
        toast.success(t('auth.resetLinkSent'));
        setForgotOpen(false);
        setForgotInput('');
      } else if (r.reason === 'no-email') {
        toast.error(t('auth.resetNoEmail'));
      } else if (r.reason === 'invalid') {
        toast.error(t('auth.resetInvalidInput'));
      } else {
        toast.error(t('auth.resetFailed'));
      }
    } finally {
      setForgotLoading(false);
    }
  }

  async function handleSendOTP(values: z.infer<typeof whatsappFormSchema>) {
    try {
      setLoading(true);
      
      // Format phone number for Firebase (add country code)
      const formattedPhone = `+966${values.phone.substring(1)}`; // Remove leading 0 and add +966
      
      const result = await sendWhatsAppOTP(formattedPhone, language as 'en' | 'ar');
      
      if (result.success && result.verificationId) {
        setVerificationId(result.verificationId);
        setOtpSent(true);
        setPhoneNumber(values.phone);
        const via = result.otpMethod ?? otpMethod;
        if (result.otpMethod) setSentVia(result.otpMethod);
        toast.success(
          via === 'sms' ? t('otp.success.otpSentSms')
            : via === 'whatsapp' ? t('otp.success.otpSentWhatsapp')
              : t('otp.success.otpSent'),
        );
      } else {
        toast.error(result.error || t('auth.sendCodeError'));
      }
    } catch (error) {
      toast.error((error as Error)?.message || t('auth.sendCodeError'));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOTP(values: z.infer<typeof whatsappFormSchema>) {
    if (verifyingRef.current) return;

    if (!values.otp) {
      toast.error(t('auth.enterCode'));
      return;
    }

    if (!verificationId) {
      toast.error(t('auth.noConfirmationResult') || 'No verification ID. Please send the code again.');
      return;
    }

    try {
      verifyingRef.current = true;
      setLoading(true);
      
      const result = await verifyWhatsAppOTP(verificationId, values.otp);
      
      if (result.success && result.userData) {
        if (result.authSessionEstablished === false) {
          setOtpFailed(true);
          toast.error(t('auth.sessionError') || 'Could not establish a secure session. Please try again.');
          return;
        }
        await loginWithWhatsAppOTP(values.phone);
        toast.success(t('auth.loginSuccess'));
      } else {
        setOtpFailed(true);
        toast.error(result.error || t('auth.verifyCodeError'));
      }
    } catch (error) {
      setOtpFailed(true);
      toast.error((error as Error)?.message || t('auth.verifyCodeError'));
    } finally {
      verifyingRef.current = false;
      setLoading(false);
    }
  }

  const onSubmitWhatsApp = async (values: z.infer<typeof whatsappFormSchema>) => {
    if (otpSent) {
      await handleVerifyOTP(values);
    } else {
      await handleSendOTP(values);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      const { userData } = await signInWithGoogle();
      setUserData(userData);
      // Navigation will be handled by AuthContext
    } catch (error) {
      if (error instanceof Error) {
        // Check if the error message is a translation key
        if (error.message.startsWith('auth.')) {
          toast.error(t(error.message));
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error(t('auth.signInError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNameInputComplete = () => {
    setShowNameDialog(false);
    setNewUserData(null);
    // Navigation will be handled by AuthContext
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    value = value.slice(0, 10);
    whatsappForm.setValue('phone', value);

    if (/^05\d{8}$/.test(value)) {
      setTimeout(() => {
        submitButtonRef.current?.click();
      }, 150);
    }
  };

  const resetWhatsAppForm = () => {
    setOtpSent(false);
    setPhoneNumber('');
    setVerificationId(null);
    setOtpFailed(false);
    setSentVia(null);
    whatsappForm.reset();
  };

  const handleOTPComplete = () => {
    if (otpFailed || verifyingRef.current) return;
    setTimeout(() => {
      submitButtonRef.current?.click();
    }, 150);
  };

  return (
    <div className="bg-card p-8 rounded-lg shadow-lg" dir={dir}>
      <div className="space-y-2 text-center mb-6">
        <h1 className="text-3xl font-bold">
          {isFromVolunteerRoute ? (t('auth.volunteerWithUs') || 'تطوع معنا') : t('common.welcomeBack')}
        </h1>
        <p className="text-muted-foreground">
          {isFromVolunteerRoute 
            ? (t('auth.loginToVolunteer') || 'سجل الدخول للتطوع معنا') 
            : t('common.enterCredentials')
          }
        </p>
      </div>

      {/* Login Method Toggle - Only show if multiple methods are available */}
      {!loginMethodsLoading && (availableMethods.whatsapp && availableMethods.password) && (
        <div className="flex rounded-lg border p-1 bg-muted mb-6">
          <button
            type="button"
            onClick={() => {
              setLoginMethod('whatsapp');
              passwordForm.reset();
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              loginMethod === 'whatsapp'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {otpMethod === 'whatsapp' ? <MessageSquare className="h-4 w-4" /> : <Smartphone className="h-4 w-4" />}
            {otpMethod === 'sms' ? t('auth.smsOTP') : otpMethod === 'whatsapp' ? t('auth.whatsappOTP') : t('auth.phoneOTP')}
          </button>
          <button
            type="button"
            onClick={() => {
              setLoginMethod('password');
              resetWhatsAppForm();
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              loginMethod === 'password'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Mail className="h-4 w-4" />
            {t('auth.emailPassword')}
          </button>
        </div>
      )}

      {/* Password Login Form */}
      {!loginMethodsLoading && loginMethod === 'password' && availableMethods.password && (
        <Form {...passwordForm}>
          <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-4 mb-2">
          <FormField
              control={passwordForm.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.emailOrPhone')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('auth.enterEmailOrPhone')}
                    disabled={loading}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
              control={passwordForm.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.password')}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t('auth.enterPassword')}
                    disabled={loading}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-2">
            <button
              type="button"
              className="text-sm text-muted-foreground hover:underline"
              onClick={() => setForgotOpen((v) => !v)}
              disabled={loading}
            >
              {t('auth.forgotPassword')}
            </button>
            {forgotOpen && (
              <div className="rounded-md border p-3 space-y-2">
                <Input
                  placeholder={t('auth.resetEmailOrPhone')}
                  value={forgotInput}
                  onChange={(e) => setForgotInput(e.target.value)}
                  disabled={forgotLoading}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    if (!forgotLoading && forgotInput.trim()) void handleForgotPassword();
                  }}
                />
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={handleForgotPassword} disabled={forgotLoading || !forgotInput.trim()}>
                    {forgotLoading ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setForgotOpen(false)} disabled={forgotLoading}>
                    {t('auth.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.loggingIn')}
              </>
            ) : (
              t('auth.login')
            )}
          </Button>
        </form>
      </Form>
      )}

      {/* WhatsApp OTP Login Form */}
      {!loginMethodsLoading && loginMethod === 'whatsapp' && availableMethods.whatsapp && (
        <Form {...whatsappForm}>
          <form onSubmit={whatsappForm.handleSubmit(onSubmitWhatsApp)} className="space-y-4 mb-2">
            {!otpSent && (
              <FormField
                control={whatsappForm.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.phone')}</FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder={t('auth.phonePlaceholder')}
                        disabled={loading}
                        className="placeholder:text-gray-300"
                        {...field}
                        onChange={handlePhoneChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {otpSent && (
              <FormField
                control={whatsappForm.control}
                name="otp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.verificationCode')}</FormLabel>
                    <FormControl>
                      <OTPInput
                        value={field.value || ''}
                        onChange={field.onChange}
                        length={4}
                        disabled={loading}
                        dir={dir}
                        className="justify-center"
                        autoFocus={true}
                        onComplete={handleOTPComplete}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <Button 
              ref={submitButtonRef}
              type="submit" 
              className="w-full flex items-center justify-center gap-2" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {otpSent ? t('auth.verifying') : t('auth.sendingCode')}
                </>
              ) : otpSent ? (
                t('auth.verify')
              ) : (
                <>
                  {otpMethod === 'whatsapp'
                    ? <MessageSquare className="h-4 w-4" />
                    : <Smartphone className="h-4 w-4" />
                  }
                  {otpMethod === 'sms' ? t('auth.sendCodeSms') : otpMethod === 'whatsapp' ? t('auth.sendCodeWhatsapp') : t('auth.sendCode')}
                </>
              )}
            </Button>

            {otpSent && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  {otpMethod === 'sms'
                  ? (t('otp.info.checkSms') || 'Please check your SMS messages')
                  : (t('otp.info.checkWhatsapp') || 'Please check your WhatsApp messages')
                } ({phoneNumber})
                </p>
              </div>
            )}
          </form>
        </Form>
      )}

      {/* Google Sign-in section */}
      {!loginMethodsLoading && loginMethodsSettings.googleLogin && (
        <>
      <Button variant="outline" className="w-full bg-gray-100 hover:bg-gray-200 border-gray-200 mb-6" onClick={handleGoogleSignIn} disabled={loading}>
        <svg className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-3'}`} viewBox="0 0 24 24">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        {t('auth.continueWithGoogle')}
      </Button>
        </>
      )}

      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          {t('auth.dontHaveAccount')}{' '}
          <Link to="/signup" className="text-primary hover:underline">
            {t('auth.signUp')}
          </Link>
        </p>
      </div>

      {showNameDialog && newUserData && (
        <NameInputDialogWithOTP
          isOpen={showNameDialog}
          onClose={() => setShowNameDialog(false)}
          userId={newUserData?.id || ''}
          onComplete={handleNameInputComplete}
        />
      )}
    </div>
  );
}